import { useCallback, useEffect, useRef } from 'react';
import { Modal, message } from 'antd';
import audioClient from '../client/audioClient';
import useTranslation from './useTranslation';

/**
 * Кэш ссылок на озвучку по ref из ответа сервера — общий для карточки слова
 * и карточек практики.
 *
 * Ссылка запрашивается по нажатию (или явным вызовом), а не при отрисовке.
 * Кэш живёт в ref, а не в состоянии: ссылки не участвуют в разметке, и держать
 * их в useState значило бы перерисовывать компонент ради данных, которых никто
 * не показывает. Отзывать нечего: это presigned-ссылки S3, они протухают сами,
 * поэтому запись живёт меньше серверного TTL и после протухания запрашивается
 * заново.
 *
 * Ключом кэша служит cacheKey вызывающего (слово|часть речи|язык): сменился —
 * прежние ссылки уже не про него. Внутри ключа записи заводятся по ref.
 * generation растёт на каждой уборке: по нему проигрывание понимает, что запрос,
 * который оно ждало, относится к уже оставленному слову.
 */
// Меньше серверного audio.presign-ttl (5 минут): ссылка из кэша не должна
// доживать до протухания.
const LINK_FRESH_MS = 4 * 60 * 1000;

const useAudioCache = (cacheKey, localeCode) => {
    const { t } = useTranslation();
    const audioCache = useRef({ key: null, generation: 0, entries: new Map() });

    const releaseAudio = useCallback(() => {
        const released = audioCache.current;
        audioCache.current = { key: null, generation: released.generation + 1, entries: new Map() };
    }, []);

    // Уборка при размонтировании и при смене слова или языка. Отпускается только
    // занятое под собственный ключ: эффекты сбрасываются после отрисовки, и нажатие
    // успевает попасть в промежуток — безусловная уборка отменила бы запрос по
    // новому слову на полпути.
    useEffect(() => () => {
        if (audioCache.current.key === cacheKey) {
            releaseAudio();
        }
    }, [cacheKey, releaseAudio]);

    /**
     * Ссылка на озвучку. Один и тот же запрос не уходит дважды: в кэше лежит промис,
     * а не готовая ссылка, поэтому второе нажатие присоединяется к первому.
     */
    const loadAudio = useCallback((ref) => {
        if (audioCache.current.key !== cacheKey) {
            if (audioCache.current.key !== null) {
                releaseAudio();
            }
            audioCache.current.key = cacheKey;
        }

        const cached = audioCache.current.entries.get(ref);
        if (cached && Date.now() - cached.fetchedAt < LINK_FRESH_MS) {
            return cached.promise;
        }

        // Карту запоминаем здесь: пока запрос идёт, слово может смениться, и тогда
        // audioCache.current будет уже другой картой, с чужими записями.
        const entries = audioCache.current.entries;
        const entry = { fetchedAt: Date.now(), promise: null };
        entry.promise = audioClient
            .getAudioLink({ ref, locale: localeCode })
            .catch((error) => {
                // Логируем здесь, а не у подписчиков: их бывает несколько, и каждый
                // молчит, чтобы не задваивать запись.
                console.error("Не удалось получить озвучку:", error);
                // Неудачный запрос из кэша убираем — но только если это всё ещё он,
                // иначе можно вычеркнуть чужую живую запись по новому слову.
                if (entries.get(ref) === entry) {
                    entries.delete(ref);
                }
                throw error;
            });

        entries.set(ref, entry);
        return entry.promise;
    }, [cacheKey, releaseAudio, localeCode]);

    const playAudioByUrl = (url) => {
        if (url) {
            const audio = new Audio(url);
            audio.play().catch((error) => {
                console.error("Ошибка при попытке воспроизведения аудио:", error);
            });
        }
    };

    /**
     * Проигрывает озвучку по ref, запрашивая ссылку при первом обращении. Мобильные
     * браузеры разрешают звук только внутри пользовательского жеста; первый запрос —
     * это маленький JSON со ссылкой, а не сам файл, так что окно узкое, а с
     * предзагрузкой по pointerdown его обычно нет вовсе.
     */
    const startPlayback = useCallback((ref) => {
        // Номер поколения читаем после loadAudio: на первом обращении он сам
        // сбрасывает кэш и увеличивает номер, и снимок «до» никогда бы не совпал.
        const pending = loadAudio(ref);
        const generation = audioCache.current.generation;
        pending
            .then((url) => {
                // Пока шёл запрос, слово могло смениться. Играть озвучку того,
                // что пользователь уже оставил, нельзя.
                if (audioCache.current.generation === generation) {
                    playAudioByUrl(url);
                }
            })
            .catch((error) => {
                // Запись об ошибке уже сделана в loadAudio; здесь только машинные
                // коды, которым нужны слова для пользователя.
                if (error?.code === 'QUOTA_EXCEEDED') {
                    message.warning(t('common.audioQuotaExceeded'));
                } else if (error?.code === 'PLAN_REQUIRED') {
                    message.info(t('common.exampleAudioPremium'));
                } else if (error?.code === 'AUDIO_UNAVAILABLE') {
                    message.info(t('common.audioUnavailable'));
                }
            });
    }, [loadAudio, t]);

    /**
     * Проигрывание по вердикту сервера. ALLOWED играет сразу. NOT_READY значит, что
     * файла заведомо нет и нажатие закажет синтез за единицу месячного лимита — такое
     * списание не должно быть сюрпризом, поэтому сначала подтверждение. Запрос ссылки
     * уходит из onOk, то есть внутри жеста нажатия по кнопке диалога.
     */
    const playAudio = useCallback((ref, access = 'ALLOWED') => {
        if (!ref) {
            return;
        }
        if (access === 'NOT_READY') {
            Modal.confirm({
                title: t('common.audioCreateTitle'),
                content: t('common.audioCreateConfirm'),
                okText: t('common.audioCreateOk'),
                cancelText: t('common.close'),
                onOk: () => startPlayback(ref),
            });
            return;
        }
        startPlayback(ref);
    }, [startPlayback, t]);

    /**
     * Запрос вдогонку к нажатию: pointerdown срабатывает раньше click, и к моменту
     * проигрывания ссылка часто уже готова — тогда звук уходит внутри жеста.
     * Для NOT_READY предзагрузки нет: запрос ссылки и есть заказ синтеза, а он
     * делается только после подтверждения.
     */
    const prefetchAudio = useCallback((ref, access = 'ALLOWED') => {
        if (!ref || access === 'NOT_READY') {
            return;
        }
        loadAudio(ref).catch(() => {
            // Молча: запись об ошибке уже сделана в loadAudio.
        });
    }, [loadAudio]);

    return { playAudio, prefetchAudio };
};

export default useAudioCache;
